import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Home, Plus, ArrowLeft, Trash2, Edit2, Upload, Camera,
  Landmark, Calendar, Euro, TrendingUp, TrendingDown,
  Download, X, Save, Eye, FileText, CreditCard, Banknote,
  ChevronLeft, ChevronRight, RefreshCw
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function CashDepositsPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // États
  const [loading, setLoading] = useState(true);
  const [deposits, setDeposits] = useState([]);
  const [stats, setStats] = useState(null);
  const [period, setPeriod] = useState('month'); // week, month, year
  const [referenceDate, setReferenceDate] = useState(new Date().toISOString().slice(0, 10));
  
  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingDeposit, setEditingDeposit] = useState(null);
  const [showDocModal, setShowDocModal] = useState(null);
  
  // Formulaire
  const [formData, setFormData] = useState({
    amount: '',
    deposit_date: new Date().toISOString().slice(0, 10),
    bank_name: 'Banque',
    reference: '',
    notes: '',
    document_data: null,
    document_name: null,
    document_type: null
  });

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    loadData();
  }, [token, period, referenceDate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const statsResponse = await axios.get(
        `${API}/api/cash-deposits/stats?period=${period}&reference_date=${referenceDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStats(statsResponse.data);
      setDeposits(statsResponse.data.deposits || []);
    } catch (error) {
      console.error('Erreur chargement:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Fichier trop volumineux (max 10 Mo)');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData({
          ...formData,
          document_data: event.target.result.split(',')[1],
          document_name: file.name,
          document_type: file.type
        });
        toast.success(`Justificatif "${file.name}" ajouté`);
      };
      reader.readAsDataURL(file);
    }
  };

  const openNewDepositModal = () => {
    setEditingDeposit(null);
    setFormData({
      amount: '',
      deposit_date: new Date().toISOString().slice(0, 10),
      bank_name: 'Banque',
      reference: '',
      notes: '',
      document_data: null,
      document_name: null,
      document_type: null
    });
    setShowModal(true);
  };

  const openEditModal = async (deposit) => {
    try {
      const response = await axios.get(`${API}/api/cash-deposits/${deposit.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const fullDeposit = response.data;
      setEditingDeposit(fullDeposit);
      setFormData({
        amount: fullDeposit.amount.toString(),
        deposit_date: fullDeposit.deposit_date,
        bank_name: fullDeposit.bank_name || 'Banque',
        reference: fullDeposit.reference || '',
        notes: fullDeposit.notes || '',
        document_data: null,
        document_name: fullDeposit.document_name,
        document_type: fullDeposit.document_type
      });
      setShowModal(true);
    } catch (error) {
      toast.error('Erreur lors du chargement du dépôt');
    }
  };

  const saveDeposit = async () => {
    if (!formData.amount || !formData.deposit_date) {
      toast.error('Veuillez remplir le montant et la date');
      return;
    }

    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount)
      };

      if (editingDeposit) {
        await axios.put(`${API}/api/cash-deposits/${editingDeposit.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Dépôt mis à jour');
      } else {
        await axios.post(`${API}/api/cash-deposits`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Dépôt enregistré');
      }
      
      setShowModal(false);
      loadData();
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const deleteDeposit = async (depositId) => {
    if (!window.confirm('Supprimer ce dépôt ?')) return;
    
    try {
      await axios.delete(`${API}/api/cash-deposits/${depositId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Dépôt supprimé');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const viewDocument = async (deposit) => {
    try {
      const response = await axios.get(`${API}/api/cash-deposits/${deposit.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.document_data) {
        setShowDocModal(response.data);
      } else {
        toast.error('Aucun justificatif pour ce dépôt');
      }
    } catch (error) {
      toast.error('Erreur lors du chargement du document');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const navigatePeriod = (direction) => {
    const date = new Date(referenceDate);
    if (period === 'week') {
      date.setDate(date.getDate() + (direction * 7));
    } else if (period === 'month') {
      date.setMonth(date.getMonth() + direction);
    } else {
      date.setFullYear(date.getFullYear() + direction);
    }
    setReferenceDate(date.toISOString().slice(0, 10));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-4">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button onClick={() => {
              sessionStorage.setItem('reports_previous_path', '/cash-deposits');
              navigate('/reports');
            }} variant="ghost" size="sm" className="text-white/70">
              <ArrowLeft className="w-4 h-4 mr-1" /> Rapports
            </Button>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Landmark className="w-6 h-6 text-emerald-400" />
              Dépôts Espèces
            </h1>
          </div>
          <Button 
            onClick={() => {
              sessionStorage.removeItem('reports_authenticated');
              navigate('/');
            }} 
            variant="ghost" 
            size="sm" 
            className="text-amber-400 hover:text-amber-300 hover:bg-amber-400/10"
          >
            <Home className="w-4 h-4 mr-1" />
            Quitter Rapports
          </Button>
        </div>

        {/* Période Navigation */}
        <div className="flex items-center justify-between mb-6 bg-white/5 rounded-xl p-4 border border-white/10">
          <div className="flex items-center gap-2">
            <Button onClick={() => navigatePeriod(-1)} variant="ghost" size="sm" className="text-white/70">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="text-center min-w-[200px]">
              <div className="text-lg font-semibold text-white">{stats?.period_label || 'Période'}</div>
              <div className="text-xs text-white/50">
                {stats?.start_date} → {stats?.end_date}
              </div>
            </div>
            <Button onClick={() => navigatePeriod(1)} variant="ghost" size="sm" className="text-white/70">
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
          
          <div className="flex items-center gap-2">
            {['week', 'month', 'year'].map((p) => (
              <Button
                key={p}
                onClick={() => setPeriod(p)}
                size="sm"
                className={period === p 
                  ? 'bg-emerald-500 hover:bg-emerald-600' 
                  : 'bg-white/10 hover:bg-white/20 text-white'
                }
              >
                {p === 'week' ? 'Semaine' : p === 'month' ? 'Mois' : 'Année'}
              </Button>
            ))}
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 border border-green-500/30 rounded-xl p-4">
              <div className="flex items-center gap-2 text-green-300 text-sm mb-1">
                <Banknote className="w-4 h-4" />
                Espèces encaissées
              </div>
              <div className="text-2xl font-bold text-white">{formatCurrency(stats.total_cash)}</div>
            </div>
            
            <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/30 rounded-xl p-4">
              <div className="flex items-center gap-2 text-blue-300 text-sm mb-1">
                <CreditCard className="w-4 h-4" />
                Carte encaissée
              </div>
              <div className="text-2xl font-bold text-white">{formatCurrency(stats.total_card)}</div>
            </div>
            
            <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border border-purple-500/30 rounded-xl p-4">
              <div className="flex items-center gap-2 text-purple-300 text-sm mb-1">
                <Euro className="w-4 h-4" />
                Total ventes
              </div>
              <div className="text-2xl font-bold text-white">{formatCurrency(stats.total_sales)}</div>
            </div>
            
            <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border border-emerald-500/30 rounded-xl p-4">
              <div className="flex items-center gap-2 text-emerald-300 text-sm mb-1">
                <Landmark className="w-4 h-4" />
                Déposé en banque
              </div>
              <div className="text-2xl font-bold text-white">{formatCurrency(stats.total_deposited)}</div>
              <div className="text-xs text-emerald-400">{stats.deposit_count} dépôt(s)</div>
            </div>
            
            <div className={`bg-gradient-to-br ${stats.cash_remaining >= 0 ? 'from-amber-500/20 to-amber-600/10 border-amber-500/30' : 'from-red-500/20 to-red-600/10 border-red-500/30'} border rounded-xl p-4`}>
              <div className={`flex items-center gap-2 ${stats.cash_remaining >= 0 ? 'text-amber-300' : 'text-red-300'} text-sm mb-1`}>
                {stats.cash_remaining >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                Espèces restantes
              </div>
              <div className="text-2xl font-bold text-white">{formatCurrency(stats.cash_remaining)}</div>
              <div className={`text-xs ${stats.cash_remaining >= 0 ? 'text-amber-400' : 'text-red-400'}`}>
                {stats.cash_remaining >= 0 ? 'À déposer' : 'Écart négatif!'}
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">
            Dépôts ({deposits.length})
          </h2>
          <Button onClick={openNewDepositModal} className="bg-emerald-500 hover:bg-emerald-600">
            <Plus className="w-4 h-4 mr-2" /> Nouveau dépôt
          </Button>
        </div>

        {/* Deposits List */}
        <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl overflow-hidden">
          {deposits.length === 0 ? (
            <div className="p-12 text-center text-white/50">
              <Landmark className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucun dépôt pour cette période</p>
              <Button onClick={openNewDepositModal} className="mt-4 bg-emerald-500 hover:bg-emerald-600">
                <Plus className="w-4 h-4 mr-2" /> Enregistrer un dépôt
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-white/10">
              {deposits.map(deposit => (
                <div key={deposit.id} className="p-4 flex items-center gap-4 hover:bg-white/5 transition">
                  <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                    <Landmark className="w-5 h-5 text-emerald-400" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-white">{formatCurrency(deposit.amount)}</div>
                    <div className="text-sm text-white/60">
                      {deposit.bank_name} • {deposit.deposit_date}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1">
                    {deposit.has_document && (
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-blue-400"
                        onClick={() => viewDocument(deposit)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="text-white/70" onClick={() => openEditModal(deposit)}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-400" onClick={() => deleteDeposit(deposit.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Modal Ajout/Modification */}
        {showModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-xl border border-gray-700 max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-white">
                    {editingDeposit ? 'Modifier le dépôt' : 'Nouveau dépôt'}
                  </h2>
                  <Button onClick={() => setShowModal(false)} variant="ghost" size="sm" className="text-gray-400">
                    <X className="w-5 h-5" />
                  </Button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-white/80 mb-1 block">Montant déposé *</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({...formData, amount: e.target.value})}
                      placeholder="0.00"
                      className="bg-white/10 border-white/20 text-white text-lg"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-white/80 mb-1 block">Date du dépôt *</label>
                      <Input
                        type="date"
                        value={formData.deposit_date}
                        onChange={(e) => setFormData({...formData, deposit_date: e.target.value})}
                        className="bg-white/10 border-white/20 text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-white/80 mb-1 block">Banque</label>
                      <Input
                        value={formData.bank_name}
                        onChange={(e) => setFormData({...formData, bank_name: e.target.value})}
                        placeholder="Nom de la banque"
                        className="bg-white/10 border-white/20 text-white"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-white/80 mb-1 block">Référence (bordereau)</label>
                    <Input
                      value={formData.reference}
                      onChange={(e) => setFormData({...formData, reference: e.target.value})}
                      placeholder="N° du bordereau"
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-white/80 mb-1 block">Notes</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      placeholder="Notes..."
                      rows={2}
                      className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white resize-none"
                    />
                  </div>
                  
                  {/* Upload justificatif */}
                  <div>
                    <label className="text-sm font-medium text-white/80 mb-2 block">Justificatif (photo bordereau)</label>
                    <div className="flex gap-2">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        accept="image/*,.pdf"
                        className="hidden"
                      />
                      <input
                        type="file"
                        ref={cameraInputRef}
                        onChange={handleFileSelect}
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                      />
                      
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-1 border-white/20 text-white"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Importer
                      </Button>
                      
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-1 border-white/20 text-white"
                        onClick={() => cameraInputRef.current?.click()}
                      >
                        <Camera className="w-4 h-4 mr-2" />
                        Scanner
                      </Button>
                    </div>
                    
                    {formData.document_name && (
                      <div className="mt-2 p-2 bg-green-500/20 rounded-lg text-green-300 text-sm flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        {formData.document_name}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <Button onClick={() => setShowModal(false)} variant="outline" className="flex-1 border-white/20 text-white">
                    Annuler
                  </Button>
                  <Button onClick={saveDeposit} className="flex-1 bg-emerald-500 hover:bg-emerald-600">
                    <Save className="w-4 h-4 mr-2" />
                    {editingDeposit ? 'Modifier' : 'Enregistrer'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal Visualisation Document */}
        {showDocModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-xl border border-gray-700 max-w-3xl w-full max-h-[90vh] overflow-hidden">
              <div className="p-4 border-b border-gray-700 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">Justificatif de dépôt</h3>
                <Button onClick={() => setShowDocModal(null)} variant="ghost" size="sm" className="text-gray-400">
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="p-4 flex items-center justify-center bg-black/30 min-h-[400px]">
                {showDocModal.document_type?.startsWith('image/') ? (
                  <img 
                    src={`data:${showDocModal.document_type};base64,${showDocModal.document_data}`}
                    alt="Justificatif"
                    className="max-w-full max-h-[70vh] object-contain"
                  />
                ) : showDocModal.document_type === 'application/pdf' ? (
                  <iframe
                    src={`data:application/pdf;base64,${showDocModal.document_data}`}
                    className="w-full h-[70vh]"
                    title="Justificatif PDF"
                  />
                ) : (
                  <div className="text-white/50">Format non supporté pour l'aperçu</div>
                )}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
